<template>
  <div class="modal" tabindex="-1" :id="id" data-backdrop="static">
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content bg-ice">
        <div class="modal-header bg-cosmos p-3">
          <span>
            <font-awesome-icon
              icon="fa-duotone fa-file-invoice-dollar"
              class="mr-2"
            />
            {{ headerTitel }}
          </span>
          <modal-close-button @confirmed="closeModal" />
        </div>

        <div class="modal-body">
          <!-- Strukturierte Ansicht (neue Response) -->
          <template v-if="hasStructuredData">
            <!-- Zusammenfassung -->
            <div class="alert alert-secondary py-2 mb-3">
              <strong>Zusammenfassung:</strong>
              <span v-if="summary.rechnungen_erstellt > 0" class="ml-2">
                <span class="badge badge-success mr-1">{{ summary.rechnungen_erstellt }}</span>
                Rechnungen
              </span>
              <span v-if="summary.personen_erstellt > 0" class="ml-2">
                <span class="badge badge-success mr-1">{{ summary.personen_erstellt }}</span>
                Personen
              </span>
              <span v-if="summary.firmen_erstellt > 0" class="ml-2">
                <span class="badge badge-success mr-1">{{ summary.firmen_erstellt }}</span>
                Firmen
              </span>
              <span v-if="summary.artikel_erstellt > 0" class="ml-2">
                <span class="badge badge-success mr-1">{{ summary.artikel_erstellt }}</span>
                Artikel
              </span>
              <span v-if="summary.verknuepfungen_erstellt > 0" class="ml-2">
                <span class="badge badge-info mr-1">{{ summary.verknuepfungen_erstellt }}</span>
                Verknüpfungen
              </span>
              <span v-if="summary.kontaktdaten_aktualisiert > 0" class="ml-2">
                <span class="badge badge-warning mr-1">{{ summary.kontaktdaten_aktualisiert }}</span>
                Kontaktdaten aktualisiert
              </span>
              <span v-if="summary.bereits_uebermittelt > 0" class="ml-2">
                <span class="badge badge-light mr-1">{{ summary.bereits_uebermittelt }}</span>
                bereits übermittelt
              </span>
              <span v-if="summary.rechnungen_fehler > 0" class="ml-2">
                <span class="badge badge-danger mr-1">{{ summary.rechnungen_fehler }}</span>
                Fehler
              </span>
            </div>

            <!-- Alle auf-/zuklappen -->
            <div v-if="invoices.length > 1" class="mb-2">
              <button
                class="btn btn-sm btn-outline-secondary mr-2"
                @click="expandAll"
              >
                <font-awesome-icon
                  icon="fa-duotone fa-angles-down"
                  class="mr-1"
                />
                Alle aufklappen
              </button>
              <button
                class="btn btn-sm btn-outline-secondary"
                @click="collapseAll"
              >
                <font-awesome-icon
                  icon="fa-duotone fa-angles-up"
                  class="mr-1"
                />
                Alle zuklappen
              </button>
            </div>

            <!-- Pro Rechnung -->
            <div
              v-for="(inv, idx) in invoices"
              :key="inv.auftrag_id || idx"
              class="card mb-2"
            >
              <div
                class="card-header pointer d-flex justify-content-between align-items-center py-2"
                :class="statusHeaderClass(inv.status)"
                @click="toggleInvoice(idx)"
              >
                <div>
                  <font-awesome-icon
                    :icon="
                      expandedInvoices[idx]
                        ? 'fa-solid fa-chevron-down'
                        : 'fa-regular fa-chevron-right'
                    "
                    class="mr-2"
                  />
                  <font-awesome-icon
                    icon="fa-duotone fa-file-invoice-dollar"
                    class="mr-2"
                  />
                  <strong>Rechnung {{ inv.auftragsnummer }}</strong>
                </div>
                <div>
                  <span
                    v-if="inv.bexio_id"
                    class="badge badge-info mr-1"
                  >Bexio ID: {{ inv.bexio_id }}</span>
                  <span
                    v-if="inv.status === 'ERROR'"
                    class="badge badge-danger"
                  >Fehler</span>
                  <span
                    v-else-if="inv.status === 'BEREITS_UEBERMITTELT'"
                    class="badge badge-light"
                  >Bereits übermittelt</span>
                  <span
                    v-else
                    class="badge badge-success"
                  >Erstellt</span>
                </div>
              </div>

              <div v-show="expandedInvoices[idx]" class="card-body py-2 px-3">
                <!-- Entity-Actions -->
                <div
                  v-for="(action, aIdx) in inv.entity_actions"
                  :key="aIdx"
                  class="d-flex align-items-start py-1 border-bottom"
                >
                  <font-awesome-icon
                    :icon="typIcon(action.typ)"
                    class="mr-2 mt-1"
                    :class="typIconColor(action.typ)"
                  />
                  <div class="flex-grow-1">
                    <span class="font-weight-bold mr-1">{{ typLabel(action.typ) }}:</span>
                    <span>{{ action.name || action.nummer || '-' }}</span>
                    <span
                      v-if="action.nummer && action.name"
                      class="text-muted ml-1"
                    >(Nr. {{ action.nummer }})</span>
                    <span
                      v-if="action.bexio_id"
                      class="text-muted ml-1"
                    >&rarr; Bexio {{ action.bexio_id }}</span>
                    <!-- Details (z.B. geänderte Felder) -->
                    <div v-if="action.details && action.details.length > 0" class="mt-1">
                      <small class="text-muted">
                        <span
                          v-for="(d, dIdx) in action.details"
                          :key="dIdx"
                          class="badge badge-light mr-1"
                        >{{ d }}</span>
                      </small>
                    </div>
                  </div>
                  <span
                    class="badge ml-2 align-self-center"
                    :class="aktionBadge(action.aktion).cssClass"
                  >{{ aktionBadge(action.aktion).text }}</span>
                </div>

                <!-- Bereits übermittelt Hinweis -->
                <div
                  v-if="inv.status === 'BEREITS_UEBERMITTELT'"
                  class="text-muted py-1"
                >
                  <font-awesome-icon
                    icon="fa-duotone fa-circle-check"
                    class="mr-1 text-secondary"
                  />
                  Diese Rechnung wurde bereits an Bexio übermittelt und wird übersprungen.
                </div>

                <!-- Keine Actions -->
                <div
                  v-else-if="!inv.entity_actions || inv.entity_actions.length === 0"
                  class="text-muted py-1"
                >
                  Keine Details verfügbar
                </div>

                <!-- Error-Detail -->
                <div
                  v-if="inv.status === 'ERROR' && inv.error_message"
                  class="alert alert-danger py-1 px-2 mt-2 mb-0"
                >
                  <font-awesome-icon
                    icon="fa-duotone fa-exclamation-circle"
                    class="mr-1"
                  />
                  {{ inv.error_message }}
                </div>
              </div>
            </div>

            <!-- Keine Rechnungen -->
            <div
              v-if="invoices.length === 0"
              class="alert alert-info"
            >
              Keine Rechnungen verarbeitet.
            </div>
          </template>

          <!-- Fallback: alte Flat-List-Ansicht -->
          <template v-else-if="hasLegacyData">
            <div v-if="legacyResponse.exception_list && legacyResponse.exception_list.length > 0">
              <span>{{ $t("global.notification_failed") }}:</span>
              <ul>
                <li
                  v-for="(errorType, etIndex) in exception_error_types"
                  :key="'et-' + etIndex"
                >
                  <span>{{ errorType }}:</span>
                  <ul>
                    <li
                      v-for="(item, itIndex) in legacyResponse.exception_list.filter(
                        (e) => e.error_type === errorType
                      )"
                      :key="'it-' + itIndex"
                    >
                      {{ item.message }}
                      <span v-if="item.path" class="ml-2">
                        <a
                          :href="item.path"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="text-primary"
                        >
                          <i class="fas fa-up-right-from-square" aria-hidden="true"></i>
                        </a>
                      </span>
                    </li>
                  </ul>
                </li>
              </ul>
            </div>
            <div v-if="legacyResponse.success_list && legacyResponse.success_list.length > 0">
              <span>{{ $t("global.successful") }}:</span>
              <ul>
                <li
                  v-for="(successType, stIndex) in success_message_types"
                  :key="'st-' + stIndex"
                >
                  <span>{{ successType }}:</span>
                  <ul>
                    <li
                      v-for="(item, siIndex) in legacyResponse.success_list.filter(
                        (i) => i.message_type === successType
                      )"
                      :key="'si-' + siIndex"
                    >
                      {{ item.message }}
                    </li>
                  </ul>
                </li>
              </ul>
            </div>
          </template>
        </div>

        <div class="modal-footer">
          <div class="row w-100">
            <div class="col-12 text-center">
              <button type="button" class="btn btn-success mr-3" @click="onOk">
                <span class="font-khand font-weight-bold">ok</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { modal } from "@/mixins/Modal";
import ModalCloseButton from "@/components/global/ModalCloseButton.vue";

export default {
  name: "ResponseModal",
  components: {
    ModalCloseButton,
  },
  props: {
    id: {
      type: String,
      default: "response-modal",
    },
    headerTitel: {
      type: String,
      default: "Bexio Export",
    },
  },
  data() {
    return {
      response: null,
      expandedInvoices: {},
      // Legacy fallback
      legacyResponse: {
        success_list: [],
        exception_list: [],
      },
      success_message_types: [],
      exception_error_types: [],
    };
  },
  mixins: [modal],
  computed: {
    hasStructuredData() {
      return (
        this.response &&
        this.response.invoice_results &&
        this.response.invoice_results.length > 0
      );
    },
    hasLegacyData() {
      return (
        this.legacyResponse &&
        ((this.legacyResponse.success_list &&
          this.legacyResponse.success_list.length > 0) ||
          (this.legacyResponse.exception_list &&
            this.legacyResponse.exception_list.length > 0))
      );
    },
    summary() {
      return this.response?.summary || {};
    },
    invoices() {
      return this.response?.invoice_results || [];
    },
  },
  mounted() {
    this.$bus.$on("openResponseModal", (payload) => {
      const data = payload.data;
      this.expandedInvoices = {};
      this.response = null;
      this.legacyResponse = { success_list: [], exception_list: [] };

      // Neue strukturierte Response
      if (data && data.invoice_results) {
        this.response = data;
        // Auto-expand fehlgeschlagene Rechnungen
        if (data.invoice_results) {
          data.invoice_results.forEach((inv, idx) => {
            if (inv.status === "ERROR") {
              this.$set(this.expandedInvoices, idx, true);
            }
          });
        }
        // Bei nur einer Rechnung: automatisch aufklappen
        if (data.invoice_results && data.invoice_results.length === 1) {
          this.$set(this.expandedInvoices, 0, true);
        }
      }

      // Legacy fallback (auch als Fallback wenn keine invoice_results)
      if (data && (data.success_list || data.exception_list)) {
        this.legacyResponse = {
          success_list: data.success_list || [],
          exception_list: data.exception_list || [],
        };
        this.mapTypes();
      }

      // Nur öffnen wenn Daten vorhanden
      if (this.hasStructuredData || this.hasLegacyData) {
        $("#" + this.id).modal({
          backdrop: "static",
          keyboard: false,
        });
      }
    });
  },
  methods: {
    closeModal() {
      $("#" + this.id).modal("hide");
      this.$emit("ok");
    },
    onOk() {
      this.closeModal();
      this.$emit("ok");
    },

    // Accordion
    toggleInvoice(idx) {
      this.$set(this.expandedInvoices, idx, !this.expandedInvoices[idx]);
    },
    expandAll() {
      this.invoices.forEach((_, i) => {
        this.$set(this.expandedInvoices, i, true);
      });
    },
    collapseAll() {
      this.expandedInvoices = {};
    },

    // Header-Farbe pro Status
    statusHeaderClass(status) {
      const map = {
        SUCCESS: "bg-success-light",
        ERROR: "bg-danger-light",
        BEREITS_UEBERMITTELT: "bg-already-light",
      };
      return map[status] || "bg-success-light";
    },

    // Badge für Aktion
    aktionBadge(aktion) {
      const map = {
        ERSTELLT: { cssClass: "badge-success", text: "Neu erstellt" },
        VORHANDEN: { cssClass: "badge-info", text: "Bereits vorhanden" },
        AKTUALISIERT: { cssClass: "badge-warning", text: "Aktualisiert" },
        INAKTIV_MARKIERT: {
          cssClass: "badge-secondary",
          text: "Als inaktiv markiert",
        },
        FEHLER: { cssClass: "badge-danger", text: "Fehler" },
        WARNUNG: { cssClass: "badge-warning", text: "Nicht erstellt" },
        NICHT_GEFUNDEN: { cssClass: "badge-danger", text: "Nicht gefunden" },
      };
      return map[aktion] || { cssClass: "badge-secondary", text: aktion };
    },

    // Icon für Entity-Typ
    typIcon(typ) {
      const map = {
        PERSON: "fa-duotone fa-user",
        FIRMA: "fa-duotone fa-building",
        ARTIKEL: "fa-duotone fa-box-open",
        VERKNUEPFUNG: "fa-duotone fa-link",
        KONTAKTDATEN: "fa-duotone fa-address-card",
        RECHNUNG: "fa-duotone fa-file-invoice-dollar",
        KOMMENTAR: "fa-duotone fa-comment",
      };
      return map[typ] || "fa-duotone fa-circle";
    },

    // Farbe für Entity-Typ Icon
    typIconColor(typ) {
      const map = {
        PERSON: "text-primary",
        FIRMA: "text-info",
        ARTIKEL: "text-secondary",
        VERKNUEPFUNG: "text-success",
        KONTAKTDATEN: "text-warning",
      };
      return map[typ] || "";
    },

    // Label für Entity-Typ
    typLabel(typ) {
      const map = {
        PERSON: "Person",
        FIRMA: "Firma",
        ARTIKEL: "Artikel",
        VERKNUEPFUNG: "Verknüpfung",
        KONTAKTDATEN: "Kontaktdaten",
        RECHNUNG: "Rechnung",
        KOMMENTAR: "Kommentar",
      };
      return map[typ] || typ;
    },

    // Legacy: mapTypes
    mapTypes() {
      this.success_message_types = [];
      this.exception_error_types = [];
      const successSet = new Set();
      const exceptionSet = new Set();
      this.legacyResponse.success_list.forEach((item) => {
        successSet.add(item.message_type);
      });
      this.legacyResponse.exception_list.forEach((item) => {
        exceptionSet.add(item.error_type);
      });
      this.success_message_types = Array.from(successSet);
      this.exception_error_types = Array.from(exceptionSet);
    },
  },
};
</script>

<style scoped>
.pointer {
  cursor: pointer;
}
.bg-success-light {
  background-color: #d4edda !important;
}
.bg-danger-light {
  background-color: #f8d7da !important;
}
.bg-already-light {
  background-color: #e9ecef !important;
}
.card-header {
  user-select: none;
}
</style>
